import type { Map as MapType } from 'mapbox-gl';
import React, {
  Suspense,
  lazy,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactMapboxGl, { MapContext } from 'react-mapbox-gl';
import ErrorBoundary from 'src/error/components/ErrorBoundary';
import styled from 'styled-components';
import { useOnClickOutside } from 'usehooks-ts';
import { isEmpty } from 'lodash';

// Components:
import {
  ExpandControl,
  LayersControl,
  ZoomControl,
} from '../../../../../../components/Map/Controls';
import { Container } from '../Container';

// Map config and styles:
import {
  HEATMAP_LAYERS,
  accessToken,
  isWebGlSupported,
} from '../../../../../../utils/mapUtils';
import {
  layersControlStyle,
  mapStyle,
  mapStyleMobile,
  zoomControlMobileStyle,
} from '../../config';
import { ACTIVE_ZOOM } from '../../constants';

// Context:
import { MobileContext } from '../../../../domain/context/MobileContext';

// Types/interfaces:
import type { PropsFromRedux } from '.';

export type TCoordinates = { long: number; lat: number };

interface Props extends PropsFromRedux {
  isOpen?: boolean;
  marginTop?: string;
}

const HotelPopup = lazy(
  () => import(/* webpackChunkName: "HotelPopup" */ '../HotelPopup'),
);

const HotelsMarkers = lazy(
  () => import(/* webpackChunkName: "HotelsMarkers" */ '../HotelMarkerList'),
);

const EmptyMap = lazy(
  () =>
    import(
      /* webpackChunkName: "EmptyMap" */ 'src/components/Mapbox/components/EmptyMap'
    ),
);

const MapBox = ReactMapboxGl({
  accessToken,
  maxZoom: 18,
  doubleClickZoom: false,
});

export const Map = (props: Props) => {
  const {
    browser,
    mapProps,
    onToggleMap,
    hasHotel,
    isOpen = false,
    marginTop,
    getImages,
    setPopup,
    hotelInfo,
    resetPopup,
  } = props;
  const { activeHotel, expanded, destinationBounds } = mapProps;
  const { coordinates } = activeHotel;

  const isMobile = useContext(MobileContext);

  const hotelPopupRef = useRef(null);
  const [clientZoom, setClientZoom] = useState(false);
  const [center, setCenter] = useState(coordinates);
  const [currentMap, setMap] = useState<MapType | undefined>(undefined);
  const [zoom, setZoom] = useState<[number] | undefined>(undefined);

  useEffect(() => {
    if (currentMap !== undefined) {
      currentMap.resize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useEffect(() => {
    const newZoom = clientZoom ? undefined : ([ACTIVE_ZOOM] as [number]);
    setZoom(newZoom);
    setCenter(activeHotel.coordinates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHotel.id]);

  useOnClickOutside(hotelPopupRef, resetPopup);
  const handleHover = (hoverItem) => {
    if (isMobile && hoverItem) {
      adjustMapPositionForPopup(hoverItem.coordinates || []);
      getImages(hoverItem.hotelId);
    }
    setPopup(hoverItem);
  };

  if (!hasHotel && !destinationBounds) return null;

  const isExpandable = browser.lessThan.infinity;
  const isSupported = isWebGlSupported();

  const onLoad = (map) => {
    setMap(map);
    map.getSource('hotels').setData({});
  };

  const adjustMapPositionForPopup = (newCenter) => {
    if (currentMap) {
      currentMap.flyTo({ center: newCenter, offset: [0, 150] });
    }
  };

  const zoomHandler = (map, zoomDiff) => {
    map.zoomTo(map.getZoom() + zoomDiff);
    handleZoom(true);
  };

  const handleZoom = (isClient) => {
    if (hotelInfo) resetPopup();
    if (!currentMap) {
      return;
    }
    if (!clientZoom) {
      if (isClient === true || currentMap.isZooming()) {
        setClientZoom(true);
      }
    }
  };

  const positionProps = hasHotel
    ? { center, zoom }
    : { fitBounds: destinationBounds };

  const expandProps = {
    isExpanded: expanded,
    toggleHandler: onToggleMap,
  };

  if (!isSupported)
    return (
      <EmptyMapContainer>
        <Suspense fallback={null}>
          <EmptyMap isMobile={isMobile} />
        </Suspense>
      </EmptyMapContainer>
    );

  const styles = isMobile ? mapStyleMobile : mapStyle;
  const layersStyle = isMobile
    ? layersControlStyle.mobile
    : layersControlStyle.desktop;

  return (
    <Container isOpen={isOpen} isMobile={isMobile}>
      <Wrapper isMobile={isMobile} expanded={expanded} $marginTop={marginTop}>
        <ErrorBoundary showDialog={false}>
          <MapBox
            {...styles}
            {...positionProps}
            onStyleLoad={onLoad}
            onZoomStart={handleZoom}
            onDragStart={resetPopup}
          >
            <MapContext.Consumer>
              {() => (
                <>
                  {!isEmpty(hotelInfo) ? (
                    <HotelPopup
                      innerRef={hotelPopupRef}
                      hoveredHotelInfo={hotelInfo}
                      onMouseLeave={resetPopup}
                    />
                  ) : null}

                  <HotelsMarkers handleHover={handleHover} />

                  {isExpandable && !isMobile && (
                    <ExpandControl
                      {...expandProps}
                      collapseText={undefined}
                      expandText={undefined}
                      position="top"
                    />
                  )}
                  <ZoomControl
                    zoomDiff={0.8}
                    onControlClick={zoomHandler}
                    style={isMobile ? zoomControlMobileStyle : null}
                  />
                  <LayersControl layers={HEATMAP_LAYERS} style={layersStyle} />
                </>
              )}
            </MapContext.Consumer>
          </MapBox>
        </ErrorBoundary>
      </Wrapper>
    </Container>
  );
};

const EmptyMapContainer = styled.div`
  width: 100%;
`;

export const Wrapper = styled.div<{
  expanded: boolean;
  isMobile: boolean;
  $marginTop?: string;
}>`
  margin-top: ${({ expanded, $marginTop }) =>
    $marginTop || (expanded ? 0 : 28)}px;
  margin-left: 8px;
  z-index: 1;
  position: sticky;
  top: 60px;
  width: 100%;

  ${({ isMobile }) =>
    isMobile &&
    `
      height: 100%;
      margin: 0;
      top: 0;
    `}
`;
